// MACHINERY V2 (Stroje) — readiness PRESENTATION ADAPTER (M2.9).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.9 + §4.1 #9.
// Reference impl: design_handoff_machinery_v3/machinery-readiness.jsx.
//
// ===================================================================
//  THIS FILE DERIVES NOTHING. IT IS AN ADAPTER, NOT A DERIVATION.
// ===================================================================
// `customer/machinery/utils/midasReadiness.ts` is the SINGLE SOURCE OF TRUTH
// for MIDAS Data Readiness across the whole app (catalog, machinery cards,
// machine profile, MIDAS new-chat picker — and now v2). Every field below is
// either
//   (a) a verbatim pass-through of a midasReadiness export, or
//   (b) a 1:1 RELABEL of one of those outputs into a closed presentation enum
//       (`aiProvenance` / `telemetry` / `interactive`),
// and nothing else. No FLAG in this file is computed from a RAW readiness
// field (docCounts, docTypes, directSources, dynamicCapability, isOemPartner) —
// every one of them comes back from midasReadiness.ts. The single exception is
// `languagesLabel`, which only FORMATS `readiness.languages` for display and
// gates nothing (see the locked-rules list below). If a fact you need is not
// exported by midasReadiness.ts, STOP and ask — do NOT compute it here. Two
// components independently deciding the same truth is the exact bug class M2.1
// shipped twice.
//
// This is the M2.9 analogue of fleet/cardStates.ts `deriveCardPresentation`:
// ONE function decides, every component consumes its output as flags, and no
// component re-tests a raw field.
//
// LOCKED RULES and where each one is enforced:
//   - Max ONE banner, gold OEM outranks AI blue -> `banner`, straight from
//     `bannerTier()`, whose return type is a single-value union. Structurally
//     unstackable: ReadinessBanner renders `banner` and nothing else.
//   - Absence = an UNLIT badge, never a hidden one -> this adapter always
//     returns `midasLit` / `liveVisual` / `oemLit` (never undefined), so the
//     badge components have no "render nothing" branch to take.
//   - Connected = static text, nothing clickable -> `interactive`, the ONLY
//     place that rule is expressed. Components read `interactive`; none of
//     them re-tests `connected`.
//   - Languages NEVER gate -> `readiness.languages` is read in exactly ONE
//     place below, to FORMAT the display string `languagesLabel`. No flag in
//     this object depends on it, and no component receives the raw VM, so a
//     language list can never light, unlight or unlock anything.
//   - ALL CTAs only in the popup -> not expressible as a flag; enforced by the
//     component split (only ReadinessPopupV2 renders a CTA element).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import {
  BannerTier,
  PopupDocType,
  EN_SUMMARY_STRINGS,
  LiveDotState,
  LiveDotVisual,
  MidasDocsSummary,
  PopupDocRow,
  ReadinessModel,
  ReadinessSummaryParts,
  ReadinessSummaryStrings,
  bannerTier,
  hasDocs,
  liveDotState,
  liveDotVisual,
  midasDocsSummary,
  midasDotLit,
  oemDotLit,
  popupDocRows,
  readinessSummaryParts,
  totalDocs,
} from "../../utils/midasReadiness";

/** Provenance bucket for the AI capability tooltip. Mirrors the designer's
 *  three-way hint (official from the manufacturer / direct for this model /
 *  via the control system) and, like the artboard, checks OEM FIRST: an
 *  OEM-partner model with direct docs reads as "official", not "direct".
 *  "unknown" = docs exist but nothing says where they came from (a total-only
 *  readiness lights no lane); the tooltip then shows NO provenance hint rather
 *  than guessing one. */
export type ReadinessAiProvenance = "oem" | "direct" | "fanout" | "none" | "unknown";

/** Telemetry bucket for the capability tooltip + popup live block. A pure
 *  relabel of `liveDotState`: "proven" and "possible" are the same SENTENCE to
 *  a customer ("can be connected") — the distinction lives in the dot visual,
 *  which comes from `liveDotVisual`, not from here. */
export type ReadinessTelemetryKind = "connected" | "possible" | "none";

export interface MachineryV2ReadinessPresentation {
  /** `bannerTier()` — one value or null. Never a list. */
  banner: BannerTier;
  midasLit: boolean;
  liveVisual: LiveDotVisual;
  liveState: LiveDotState;
  oemLit: boolean;
  /** Real gateway link (instance surfaces only). Catalog callers omit ctx. */
  connected: boolean;
  /** The "connected = static text, nothing clickable" rule, in one place. */
  interactive: boolean;
  docCount: number;
  hasAnyDocs: boolean;
  aiProvenance: ReadinessAiProvenance;
  telemetry: ReadinessTelemetryKind;
  /** Popup lanes, already ordered + opt-in-filtered by the util. */
  docRows: PopupDocRow[];
  /** Profile-card "Midas docs" value mode (types / count / none). */
  docsSummary: MidasDocsSummary;
  /** Doc phrase ("3 docs") + strongest signal, from the util's precedence. */
  summary: ReadinessSummaryParts;
  /**
   * Formatted model-level language metadata ("EN - DE - SK"), or null.
   * INFORMATIONAL ONLY — the popup prints it beside a lit lane and nothing
   * else in this object is derived from it (locked: languages never gate).
   * Carried here so no component ever needs the raw ReadinessVM.
   */
  languagesLabel: string | null;
}

/**
 * Fan-out vs direct is decided in exactly ONE place — `popupDocRows()` — and
 * read back off its OUTPUT here, never re-tested from `brandFanoutSources`.
 * That is what makes the badge tooltip and the popup lanes unable to disagree
 * BY CONSTRUCTION: if no lane says fan-out, the tooltip cannot say fan-out.
 *
 * The bug this closes (review finding 4): the old expression fell through to
 * "fanout" whenever `hasDirectDocs()` was false, while `popupDocRows` decides
 * fan-out from `brandFanoutSources.length > 0`. A model with
 * `docCounts:{manual:1}`, `directSources:[]`, `brandFanoutSources:[]`, non-OEM
 * made the tooltip say "via the control-system package" while the popup's
 * Manual lane said "direct" — the exact split-brain this adapter exists to
 * prevent. Same discipline as M2.1's deriveCardPresentation: ONE decision,
 * consumed as output, never a second opinion on the raw fields.
 */
const aiProvenanceFrom = (
  rows: PopupDocRow[],
  oemLit: boolean,
  anyDocs: boolean
): ReadinessAiProvenance => {
  // >>> DO NOT HOIST THE `oemLit` TEST ABOVE THIS LINE. <<< It looks like the
  // one-line fix for review finding F2 (an OEM model with no docs reading as
  // "none") and it is the wrong one: this value is the PROVENANCE OF
  // DOCUMENTS, and a model with none has no provenance to report — claiming
  // "official from the manufacturer" would make CapabilityBadge's tooltip
  // assert a source for documents that do not exist. F2 is fixed in
  // readiness/readinessTier.ts, which reads `oemLit` BESIDE this value.
  if (!anyDocs) return "none";
  if (oemLit) return "oem";
  const lit = rows.filter((r) => r.lit);
  if (lit.some((r) => r.provenance === "direct-oem" || r.provenance === "direct-owner")) {
    return "direct";
  }
  if (lit.some((r) => r.provenance === "fanout")) return "fanout";
  return "unknown";
};

export interface ReadinessLiveContext {
  connected?: boolean;
}

/**
 * The ONE readiness derivation entry point for v2. Every M2.9 component takes
 * this object (or a field of it) and renders; none of them calls a
 * midasReadiness DERIVATION, and none re-tests a raw readiness field. (The one
 * direct call anywhere in the kit is ReadinessPopupV2 -> fanoutProvenanceLabel,
 * a per-row label LOOKUP that decides nothing and cannot be hoisted here
 * because the fan-out source is per doc row, not per model.)
 *
 * Pass the TRANSLATED `strings` bank (readinessSummaryStrings) on any i18n
 * surface — the default is English, exactly as midasReadiness.ts itself
 * defaults, and `summary.docPart` is rendered by three components.
 */
export function deriveReadinessPresentation(
  model: ReadinessModel,
  ctx?: ReadinessLiveContext,
  strings: ReadinessSummaryStrings = EN_SUMMARY_STRINGS
): MachineryV2ReadinessPresentation {
  const readiness = model.readiness;
  const liveState = liveDotState(readiness, ctx);
  const oemLit = oemDotLit(model);
  const anyDocs = hasDocs(readiness);
  const connected = liveState === "connected";
  // Computed ONCE and used for BOTH the popup lanes and the tooltip
  // provenance — the two can therefore never disagree (see aiProvenanceFrom).
  const docRows = popupDocRows(model);

  return {
    banner: bannerTier(model),
    midasLit: midasDotLit(readiness),
    liveVisual: liveDotVisual(readiness, ctx),
    liveState,
    oemLit,
    connected,
    interactive: !connected,
    docCount: totalDocs(readiness),
    hasAnyDocs: anyDocs,
    aiProvenance: aiProvenanceFrom(docRows, oemLit, anyDocs),
    telemetry:
      liveState === "connected"
        ? "connected"
        : liveState === "proven" || liveState === "possible"
        ? "possible"
        : "none",
    docRows,
    docsSummary: midasDocsSummary(readiness),
    summary: readinessSummaryParts(model, ctx, strings),
    languagesLabel:
      readiness && readiness.languages && readiness.languages.length > 0
        ? readiness.languages.map((l) => l.toUpperCase()).join(" · ")
        : null,
  };
}

/**
 * Translated word bank for `readinessSummaryParts`. The util owns the
 * PRECEDENCE and the markers; this only swaps the WORDS, and the two
 * "... available" entries are full gender-correct phrases because Slavic
 * languages inflect the adjective for the noun (midasReadiness.ts item 14).
 * Keys are the DR-4 ones that already ship in en + sk.
 */
export const readinessSummaryStrings = (
  translate: (key: string) => string
): ReadinessSummaryStrings => ({
  doc: translate("myMachinery:summaryDoc"),
  docs: translate("myMachinery:summaryDocs"),
  oem: translate("myMachinery:summaryOem"),
  oemAvailable: translate("myMachinery:summaryOemAvailable"),
  telemetryAvailable: translate("myMachinery:summaryTelemetryAvailable"),
  live: translate("myMachinery:summaryLive"),
});

/**
 * Doc-type -> i18n key (myMachinery namespace), canonical order.
 *
 * The shipped v1 feature has the same map, but it lives in
 * `components/readiness/readinessPopupParts.tsx`, which imports MUI — and the
 * v2 stack forbids MUI anywhere in its import graph (ticket §3). So the map is
 * restated here rather than imported. It is safe to restate because it is a
 * `Record<PopupDocType, string>`: if midasReadiness.ts ever adds a sixth doc
 * type, BOTH copies fail to compile, which is exactly the alarm we want.
 */
export const V2_DOC_TYPE_KEYS: Record<PopupDocType, string> = {
  manual: "myMachinery:docTypeManual",
  electrical: "myMachinery:docTypeElectrical",
  alarms: "myMachinery:docTypeAlarms",
  specSheet: "myMachinery:docTypeSpecSheet",
  oemNodeDocs: "myMachinery:docTypeOemNodeDocs",
};

/** Pinned cross-palette readiness hexes (the handoff's `RD_C`). These are
 *  brand SIGNAL colours — MIDAS blue, live green, OEM gold — and are
 *  deliberately NOT palette tokens: they must read identically under both
 *  palettes, exactly as the designer pinned them (machinery-readiness.jsx). */
export const RD = {
  midas: "#3498DB",
  live: "#27AE60",
  oem: "#C9962E",
  hollow: "#B9BEC6",
  off: "#DADEE3",
  offText: "#C4C8CE",
  link: "#2E90E5",
  goldBg: "#FBF4DF",
  goldText: "#A87F1E",
  goldStar: "#B98A1F",
  goldLine: "#E8D9AE",
  blueBg: "#EBF5FB",
  blueText: "#2E86DE",
  blueSpark: "#3498DB",
  blueLine: "#AED6F1",
};
